/**
 * File management hooks — ID-based navigation
 *
 * Navigate by folder ID (null = root /papan/).
 * All operations use file IDs instead of paths.
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { isSessionEndedError, errorText } from '@papan/api-client'
import { filesApi, listingNeedsApproval, MalwareDetectedError, runEmptyBinJob, runBinPurgeJob, type BinPurgeRunHooks, type ConflictStrategy, type FileListItem, type BinResponse, type BinItem, onListedRowRefused } from '@papan/files/client'
import { canPatchFileEvent, fetchAndCacheList, fileKeys, IdentityChangedError, normalizeEventParentId } from '@papan/files/client/queries'
import { useQueryClient } from '@tanstack/react-query'
import { useRealtime, type FileEvent, type FileEventRow } from '@papan/realtime/client'
import { isProjectFolder, isChainPointerRow, isSharedPointerRow, isSharedPointer, isBinNudge, binNudgeSharedIn, isMountFileId } from '@papan/types'

/** Sort priority: folders first (excluding project folders), then project folders, then files. */
function fileSortKey(item: { name: string; isFolder: boolean }): number {
  if (isProjectFolder(item.name)) return 1
  if (item.isFolder) return 0
  return 2
}
/**
 * Client mirror of the server listing collation (`listChildrenPaginated`):
 * tier first (folders / project folders / files — the client-side overlay on
 * the server's plain folders-first), then the ACTIVE sort field + direction.
 * The field comparison matters for realtime inserts: the patch path appends
 * the new row before sorting, and with a tier-only comparator the stable sort
 * left it at the bottom of its tier — a restored/created file showed up last
 * instead of in its sorted spot until the next refetch.
 */
function sortFileItems<T extends { name: string; isFolder: boolean; size?: number; updatedAt?: string }>(
  items: T[],
  sortBy: SortField = 'name',
  sortDir: SortDirection = 'asc',
): T[] {
  // Space → ~ mirrors the server's REPLACE(name, ' ', '~') so "styles.css"
  // sorts before "styles (1).css".
  const nameKey = (n: string) => n.toLowerCase().replace(/ /g, '~')
  const extKey = (t: T) =>
    t.isFolder ? '' : nameKey(t.name.includes('.') ? t.name.slice(t.name.lastIndexOf('.') + 1) : t.name)
  return [...items].sort((a, b) => {
    const tier = fileSortKey(a) - fileSortKey(b)
    if (tier !== 0) return tier
    let d = 0
    switch (sortBy) {
      case 'modified': d = (Date.parse(a.updatedAt ?? '') || 0) - (Date.parse(b.updatedAt ?? '') || 0); break
      case 'size': d = (a.size ?? 0) - (b.size ?? 0); break
      case 'type': d = extKey(a) < extKey(b) ? -1 : extKey(a) > extKey(b) ? 1 : 0; break
      default: d = nameKey(a.name) < nameKey(b.name) ? -1 : nameKey(a.name) > nameKey(b.name) ? 1 : 0
    }
    return sortDir === 'desc' ? -d : d
  })
}

/**
 * Generic hook for async list data with loading state and refresh.
 */
function useAsyncList<T>(fetchFn: () => Promise<T[]>) {
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchFn()
      setItems(data)
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [fetchFn])

  useEffect(() => {
    load()
  }, [load])

  return { items, loading, refresh: load }
}

export interface UploadResult {
  success: boolean
  fileId?: string
  name?: string
  error?: string
  threat?: string
  skipped?: boolean
}

export interface FileToUpload {
  file: File
  parentId: string | null
  conflictStrategy?: ConflictStrategy
}

export interface UploadFilesResult {
  successCount: number
  results: UploadResult[]
}

/** Controller interface for grouped upload progress (from useToast().uploadGroup()) */
export interface UploadGroupController {
  addFile: (fileId: string, name: string, total: number) => void
  updateFile: (fileId: string, loaded: number) => void
  completeFile: (fileId: string) => void
  failFile: (fileId: string, error: string) => void
  cancelFile: (fileId: string) => void
  getAbortController: (fileId: string) => AbortController
  complete: (message?: string) => void
  id: string
}

export interface UploadProgress {
  loaded: number
  total: number
  percentage: number
}

export type FileType =
  | 'folder' | 'workpapan' | 'shared'
  // Spreadsheets
  | 'csv' | 'tsv' | 'xlsx' | 'xls' | 'ods' | 'parquet'
  // Documents
  | 'docx' | 'doc' | 'odt' | 'rtf' | 'pdf'
  // Presentations
  | 'pptx' | 'ppt' | 'odp'
  // Code / markup
  | 'json' | 'md' | 'html' | 'xml' | 'yaml' | 'code'
  // Text
  | 'txt' | 'log' | 'ppn' | 'pmeta'
  // Media
  | 'png' | 'jpg' | 'gif' | 'webp' | 'svg' | 'mp4' | 'webm' | 'mov'
  // Audio — one type per format; each gets its own colour within a shared
  // warm palette (distinct from video's cool lavender, and from each other).
  | 'mp3' | 'wav' | 'm4a' | 'aac' | 'flac' | 'aiff' | 'opus'
  // Archive
  | 'zip'
  // Database
  | 'db'
  // Special
  | 'sqlparz'
  | 'unknown'

export interface FileItem {
  id: string                    // File/folder ID (real database ID)
  name: string
  parentId: string | null
  type: FileType
  isFolder: boolean
  size: number
  mimeType: string | null
  updatedAt: string
  // Caller's resolved permission on this row, inlined by the list/detail
  // endpoints. Lets per-row context menus gate "Who has access..." etc. on
  // the row itself rather than the navigated folder. Absent on rows built
  // from realtime events — callers must fall back to the folder-level gate.
  effectivePermission?: string
  // True for a `.shared` pointer whose cross-tenant grant is gone
  // (revoked / broken). The UI greys it out as "No longer available" and
  // blocks opening; the recipient can still delete it.
  shareBroken?: boolean
  // Refines shareBroken's LABEL to "No access" for a self-supplied IMPORT
  // `.shared` (never granted) — vs a once-live share's "No longer available".
  // Always paired with shareBroken (same greyed/blocked behavior).
  shareNoAccess?: boolean
  /** (#327) The source owner shared this mount WITHOUT reshare rights, so
   *  sharing it onward is refused server-side. Greys the share verbs. */
  reshareBlocked?: boolean
  // DB row-kind column ('shared' / 'chain' for real mount artifacts;
  // 'file' / 'folder' otherwise). Drives the icon overlay decision so a
  // user-renamed "foo.shared" file doesn't get the Network badge.
  fileType?: string | null
  // Raw `metadata_json` for the row (same-tenant listings) or its
  // `miniappIcon`-key subset (cross-tenant / share listings). Drives the
  // custom mini-app icon via `parseMiniappIcon`. Rides realtime `file:updated`
  // events so an icon edit patches open lists live. Absent when there's none.
  metadataJson?: string | null
  // Source-tenant display name for a real `.shared` row, supplied by
  // the listing endpoint. Drives the "Shared by <Org>" label that sits
  // next to the SHARED badge. Absent on non-.shared rows and on rows
  // synthesized from realtime events (callers may refresh to fill it).
  sharedFromName?: string
  // True when THIS tenant owns the folder and at least one active
  // share_grants row points to an outsider. Drives the SHARING badge
  // + Share2 corner overlay in the owner's drive view. Absent on
  // recipient `.shared` rows (those use sharedFromName instead).
  hasOutboundShare?: boolean
  // Set alongside hasOutboundShare when the flag comes ONLY from a shared
  // ancestor (no direct share_grants row on this item). Such a row has no
  // entry of its own in the Who-has-access dialog, so the SHARING badge
  // renders dimmed to keep the distinction visible.
  outboundShareInherited?: boolean
  // "Points-to" fields for a `.chain` row, resolved server-side from the
  // same-tenant target. Drive the "→ <target>" label + click-through.
  chainTargetName?: string
  chainTargetId?: string
  chainTargetIsFolder?: boolean
  // True for a `.chain` whose target no longer resolves — greyed out as
  // "No longer available", opening blocked (still deletable).
  chainBroken?: boolean
  // D10: a live `.chain` no person vouched for (or whose author lost its target) — share
  // links and apps don't follow it until someone who can open and share the target approves it.
  chainNeedsApproval?: boolean
  // …and the viewer can open and share the target, so they may approve it.
  chainCanApprove?: boolean
  /** Owner 2026-10-27: a code file the app (or a visitor) wrote — Needs approval before it runs as the app. */
  codeNeedsApproval?: boolean
  codeCanApprove?: boolean
  /** A folder with a code file needing approval somewhere below it (the listing's `codeApprovalBelow`). */
  codeApprovalBelow?: boolean
  /** The listed content's hash — an Approve names the version the person saw (POST /:id/code/approve). */
  contentHash?: string | null
  // Issue #157 v2: a DEMOTED share copy — a plain file named `*.shared` whose
  // target already has a live canonical `.shared`. Renders a "Conflict"
  // pill (visible, not greyed).
  alreadyShared?: boolean
  /** D118 (owner 2026-10-20): this app's (or this) databases.json can't be read — its databases are held back. Only for
   *  whoever can edit the app. `path` = the manifest from the app; `manifestRowId` opens it. */
  manifestProblem?: { path: string; reason: string; manifestRowId: string }
  // Creator = created_by person (Details only — never shown as access, #1037);
  // accessSummary = direct grants/links. Both managers only. Absent on rows
  // synthesized from realtime events (like effectivePermission — can't ride
  // the broadcast payload; fills on the next refresh).
  creator?: { name: string; email?: string }
  accessSummary?: {
    teams: { name: string; isMine: boolean }[]
    teamCount: number
    people: string[]
    peopleCount: number
    links: number
    outsiders: number
  }
  floor?: 'member' | 'admin'
  // #781: a delete job is running on this folder (server marker). Renders
  // as "Deleting…" for everyone, not only the tab that clicked.
  deleting?: boolean
}

function getFileType(name: string): FileType {
  const ext = name.split('.').pop()?.toLowerCase()
  switch (ext) {
    // Spreadsheets
    case 'csv': return 'csv'
    case 'tsv': return 'tsv'
    case 'xlsx': return 'xlsx'
    case 'xls': return 'xls'
    case 'ods': case 'fods': case 'ots': return 'ods'
    case 'parquet': return 'parquet'
    // Documents
    case 'docx': return 'docx'
    case 'doc': return 'doc'
    case 'odt': case 'fodt': case 'ott': return 'odt'
    case 'rtf': return 'rtf'
    case 'pdf': return 'pdf'
    // Presentations
    case 'pptx': return 'pptx'
    case 'ppt': return 'ppt'
    case 'odp': case 'fodp': case 'otp': return 'odp'
    // Code / markup
    case 'json': return 'json'
    case 'md': case 'markdown': return 'md'
    case 'html': case 'htm': return 'html'
    case 'xml': return 'xml'
    case 'yaml': case 'yml': return 'yaml'
    // Source code / scripts / config — one unified `code` type; the badge
    // still shows the real extension label (TS / PY / SQL / …), just in one
    // shared colour. Editor/preview routing stays per-extension elsewhere.
    case 'ts': case 'tsx': case 'js': case 'jsx': case 'mjs': case 'cjs':
    case 'py': case 'rb': case 'go': case 'rs': case 'java': case 'kt':
    case 'c': case 'h': case 'cpp': case 'cc': case 'hpp': case 'cs':
    case 'php': case 'swift': case 'dart': case 'lua': case 'r': case 'pl':
    case 'sh': case 'bash': case 'zsh': case 'ps1': case 'bat':
    case 'sql': case 'css': case 'scss': case 'sass': case 'less':
    case 'vue': case 'svelte': case 'astro':
    case 'toml': case 'ini': case 'cfg': case 'conf':
    case 'jsonl': case 'patch': case 'diff':
      return 'code'
    // Text
    case 'txt': return 'txt'
    case 'log': return 'log'
    case 'ppn': return 'ppn'
    case 'pmeta': return 'pmeta'
    // Images
    case 'png': return 'png'
    case 'jpg': case 'jpeg': return 'jpg'
    case 'gif': return 'gif'
    case 'webp': return 'webp'
    case 'svg': return 'svg'
    // Video
    case 'mp4': return 'mp4'
    case 'webm': return 'webm'
    case 'mov': return 'mov'
    // Audio — one type per format so the badge colour tells them apart.
    case 'mp3': return 'mp3'
    case 'wav': return 'wav'
    case 'm4a': return 'm4a'
    case 'aac': return 'aac'
    case 'flac': return 'flac'
    case 'aiff': case 'aif': return 'aiff'
    case 'opus': return 'opus'
    // Archive
    case 'zip': return 'zip'
    // Database
    case 'db': case 'sqlite': case 'sqlite3': return 'db'
    // Special
    case 'sqlparz': return 'sqlparz'
    default: return 'unknown'
  }
}

function fileListItemToFileItem(item: FileListItem): FileItem {
  return {
    id: item.id,
    name: item.name,
    parentId: item.parentId,
    // `.shared` row-kind is authoritative — a user-renamed `foo.shared`
    // plain file/folder keeps its normal type (and no SHARED badge).
    // A file-target `.chain` is a metadata file named `<target>.chain`;
    // derive its base icon from the TARGET (e.g. a `.txt` target → txt
    // icon) so it doesn't render as 'unknown' — the chain overlay badge
    // (FileIcon, keyed on fileType) sits on top. Folder-chains take the
    // 'folder' branch via the server's lifted `isFolder`. A chain that Needs approval is
    // its own metadata only (D24): never an icon from its target — the server sends no
    // target name for it, and this never reads one.
    type: isSharedPointerRow(item)
      ? 'shared'
      : item.isFolder
        ? (isProjectFolder(item.name) ? 'workpapan' : 'folder')
        : (isChainPointerRow(item) && item.chainTargetName && !item.chainNeedsApproval)
          ? getFileType(item.chainTargetName)
          // Issue #157 v2: a DEMOTED `*.shared` file (fileType != 'shared') is a
          // plain JSON file — derive its type from the INNER name, not the
          // `.shared` suffix (else getFileType keys on `.shared` → 'shared' and
          // it wrongly renders the SHARED icon).
          : (!isSharedPointerRow(item) && isSharedPointer(item.name))
            ? getFileType(item.name.slice(0, -'.shared'.length))
            : getFileType(item.name),
    isFolder: item.isFolder,
    size: item.size,
    mimeType: item.mimeType,
    updatedAt: item.updatedAt,
    effectivePermission: item.effectivePermission,
    shareBroken: (item as { shareBroken?: boolean }).shareBroken,
    shareNoAccess: (item as { shareNoAccess?: boolean }).shareNoAccess,
    reshareBlocked: (item as { reshareBlocked?: boolean }).reshareBlocked,
    fileType: item.fileType,
    metadataJson: item.metadataJson,
    sharedFromName: item.sharedFromName,
    hasOutboundShare: (item as { hasOutboundShare?: boolean }).hasOutboundShare,
    outboundShareInherited: (item as { outboundShareInherited?: boolean }).outboundShareInherited,
    chainTargetName: item.chainTargetName,
    chainTargetId: item.chainTargetId,
    chainTargetIsFolder: item.chainTargetIsFolder,
    chainBroken: (item as { chainBroken?: boolean }).chainBroken,
    chainNeedsApproval: (item as { chainNeedsApproval?: boolean }).chainNeedsApproval,
    chainCanApprove: (item as { chainCanApprove?: boolean }).chainCanApprove,
    codeNeedsApproval: (item as { codeNeedsApproval?: boolean }).codeNeedsApproval,
    codeCanApprove: (item as { codeCanApprove?: boolean }).codeCanApprove,
    codeApprovalBelow: (item as { codeApprovalBelow?: boolean }).codeApprovalBelow,
    contentHash: (item as { contentHash?: string | null }).contentHash,
    alreadyShared: (item as { alreadyShared?: boolean }).alreadyShared,
    ...((item as { manifestProblem?: FileItem['manifestProblem'] }).manifestProblem ? { manifestProblem: (item as { manifestProblem?: FileItem['manifestProblem'] }).manifestProblem } : {}),
    creator: item.creator,
    accessSummary: item.accessSummary,
    floor: item.floor,
    ...(item.deleting ? { deleting: true } : {}),
  }
}

/**
 * Map a realtime `FileEventRow` payload to the `FileItem` shape rendered by
 * the local accumulator. Mirrors the wire-format → cache mapping done by
 * `eventRowToListItem` in queries.ts and adds the UI-level `type` derivation.
 */
function eventRowToFileItem(row: FileEventRow): FileItem {
  return {
    id: row.id,
    name: row.name,
    parentId: row.parentId,
    // Same rule as fileListItemToFileItem — gate the 'shared' UI-type
    // on the row-kind column, not the extension.
    type: isSharedPointerRow(row)
      ? 'shared'
      : row.isFolder
        ? (isProjectFolder(row.name) ? 'workpapan' : 'folder')
        : getFileType(row.name),
    isFolder: row.isFolder,
    size: row.size,
    mimeType: row.mimeType,
    updatedAt: row.updatedAt,
    fileType: row.fileType,
    // Lifts a `.miniapp` folder's custom icon from a live `file:updated` event.
    metadataJson: row.metadataJson,
    // #781: always SET (true or false), never omitted — `file:updated` MERGES
    // into the existing row, so an absent key would leave a released folder
    // stuck on "Deleting…".
    deleting: row.deleting === true,
  }
}

export type SortField = 'name' | 'modified' | 'size' | 'type'
export type SortDirection = 'asc' | 'desc'

const PAGE_SIZE = 100

/**
 * Main file management hook. Navigate by folder ID (null = root).
 */
/** How many times one load asks while the identity keeps changing under it (#1210). */
const IDENTITY_ASKS = 3

export function useFiles(initialFolderId: string | null = null) {
  const queryClient = useQueryClient()
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(initialFolderId)
  const [files, setFiles] = useState<FileItem[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState(0)
  const [sortBy, setSortBy] = useState<SortField>('name')
  const [sortDir, setSortDir] = useState<SortDirection>('asc')
  const [currentOffset, setCurrentOffset] = useState(0)
  // Server-elevated parent permission. For hallway folders (stored = 'hidden'
  // but user has accessible descendants), the server reports 'readcopy' here
  // so UI gates (copy/cut/paste) reflect what the user can actually do.
  // Reading the stored permission via getFileStats misses this elevation.
  const [parentPermission, setParentPermission] = useState<string | undefined>(undefined)
  // (#327) Set while standing INSIDE a mount whose governing grant withholds
  // reshare rights. The row-level `reshareBlocked` can't cover this case —
  // from in here the pointer row is in the PARENT listing, out of view.
  const [parentReshareBlocked, setParentReshareBlocked] = useState(false)
  // D24: the folder being listed is a `.chain` that Needs approval (or is reached
  // through one) — nobody follows it, so the server answers an EMPTY listing with
  // `mount.reason === 'needs-approval'`. That empty list is not an empty folder; the
  // view must say why (a deep link / bookmark to the chain lands here).
  const [parentNeedsApproval, setParentNeedsApproval] = useState(false)

  // Sync immediately when URL-driven folder ID changes (no flicker)
  if (initialFolderId !== currentFolderId) {
    setCurrentFolderId(initialFolderId)
    setFiles([])
    setLoading(true)
    setError(null)
    setParentNeedsApproval(false)
  }

  // Was the last listing loaded a `.shared`/`.chain` mount (its `mount` descriptor)? Keyed by folder (#1209 I3).
  const mountListingRef = useRef<{ folderId: string | null; mount: boolean }>({ folderId: null, mount: false })
  // #1210 / #1316: only the NEWEST load writes the list — an older one landing later (an identity re-ask, its answer,
  // or its session-ended drop) never overwrites it; and a load dropped because its session ended (an identity / tenant
  // switch while this view stays mounted) is asked again in the new session instead of showing "the session ended" as
  // the folder's error.
  const loadSeqRef = useRef(0)

  const loadFiles = useCallback(async (
    folderId: string | null,
    sort?: { sortBy: SortField; sortDir: SortDirection },
  ): Promise<void> => {
    const seq = ++loadSeqRef.current
    const latest = () => seq === loadSeqRef.current
    setLoading(true)
    setError(null)
    const sb = sort?.sortBy ?? sortBy
    const sd = sort?.sortDir ?? sortDir
    try {
      const opts = { limit: PAGE_SIZE, offset: 0, sortBy: sb, sortDir: sd }
      // #1210: an answer for an identity that is no longer active (an org-email switch mid-read — the drive
      // doesn't remount on one) is asked again as the identity now active, with the sort THIS call resolved — up to
      // IDENTITY_ASKS times; refused on every one, it fails like any load (never a silently empty folder).
      // A TENANT change is never asked again: the folder belongs to the other tenant (its own load follows the switch).
      let result!: Awaited<ReturnType<typeof fetchAndCacheList>>
      for (let ask = 1; ; ask++) {
        try { result = await fetchAndCacheList(queryClient, folderId, opts); break } catch (err) {
          if (!(err instanceof IdentityChangedError) || err.tenantChanged || ask >= IDENTITY_ASKS) throw err
        }
      }
      if (!latest()) return
      const items = sortFileItems(result.files.map(fileListItemToFileItem), sb, sd)
      setFiles(items)
      setCurrentFolderId(folderId)
      setHasMore(result.hasMore)
      setTotal(result.total)
      setCurrentOffset(items.length)
      setParentPermission(result.parentPermission)
      setParentReshareBlocked(!!result.mount?.reshareBlocked)
      setParentNeedsApproval(listingNeedsApproval(result))
      // #1209 I3: a mount ROOT (`Acme Docs.shared` opened) has the pointer's native id and, while broken, no rows —
      // only this descriptor says it is a mount. A failed load keeps the last answer for the same folder.
      mountListingRef.current = { folderId, mount: !!result.mount }
    } catch (err) {
      if (!latest()) return
      // #1316: dropped at a session end — every view of this hook sits in a remounting deck, so the remount reloads it
      if (isSessionEndedError(err)) return
      // a tenant change: nothing to show for the old tenant's folder — the switch's own load writes the view
      if (err instanceof IdentityChangedError && err.tenantChanged) return
      // an identity refusal is not a message for people (#1210)
      setError(err instanceof IdentityChangedError ? 'Failed to load files' : errorText(err, 'Failed to load files') ?? '')   // a drop returned above
      setFiles([])
      setHasMore(false)
      setTotal(0)
      setCurrentOffset(0)
      setParentPermission(undefined)
      setParentReshareBlocked(false)
      setParentNeedsApproval(false)
    } finally {
      if (latest()) setLoading(false)
    }
  }, [sortBy, sortDir, queryClient])

  useEffect(() => {
    loadFiles(currentFolderId)
  }, [currentFolderId]) // eslint-disable-line react-hooks/exhaustive-deps

  const navigate = useCallback((folderId: string | null) => {
    loadFiles(folderId)
  }, [loadFiles])

  const refresh = useCallback(async () => {
    // refresh() is called after upload/delete/move or a realtime file:* event,
    // all of which mean the cache is known to be out of date. Invalidate the
    // matching list keys first so the next fetch bypasses the 30s staleTime.
    await queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', currentFolderId], refetchType: 'none' })
    await loadFiles(currentFolderId)
  }, [loadFiles, currentFolderId, queryClient])

  // ─── Realtime file events (Phase A.2) ───────────────────────────────
  //
  // Server emits `file:*` events with the affected row inline (Phase A.1).
  // The TanStack list caches are patched app-wide by `FileListCacheSync`
  // (#956), never here; this hook only keeps its own accumulator (the open
  // folder's loaded pages) in step. When the event carries enough info it is
  // spliced in directly, which removes the round trip that previously ran on
  // every event. Events without `file` (older publishers, ambiguous deletes)
  // fall through to the existing debounced refresh().
  //
  // The 500ms debounce only applies to the fallback path now — fast-path
  // patches apply immediately, so a same-tab upload's row appears without
  // the half-second pause that would otherwise be visible to the user.
  const { onFileEvent, onResync } = useRealtime()
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Hold the latest currentFolderId in a ref so the subscription handler
  // doesn't go stale during navigation.
  const currentFolderIdRef = useRef(currentFolderId)
  currentFolderIdRef.current = currentFolderId
  // Hold the latest `files` in a ref so we can synchronously check
  // membership before queueing setState calls. Without this, the local
  // total could drift off by one whenever the dedup branch in setFiles
  // matches but setTotal still increments unconditionally — visible as
  // "Load more (N+1 of N)" until the next refresh corrects it.
  const filesRef = useRef(files)
  filesRef.current = files
  // Active sort, by ref for the same staleness reason as currentFolderIdRef —
  // realtime inserts must land at the position the CURRENT sort dictates.
  const sortRef = useRef({ sortBy, sortDir })
  sortRef.current = { sortBy, sortDir }

  // Refresh a SPECIFIC folder after work that targeted it (an upload). Unlike
  // `refresh()`, this reloads the visible list ONLY when the target is the
  // folder the user is currently viewing; otherwise it just invalidates that
  // folder's cache so its next visit refetches. A background upload into a
  // different folder must not touch navigation — calling loadFiles() would run
  // setCurrentFolderId() for the wrong folder, and the URL-vs-state guard above
  // then clears the open folder to "Loading… / 0 items" (#180).
  //
  // The comparison reads the LIVE folder via currentFolderIdRef, never a
  // closed-over currentFolderId: the upload's onComplete callback is captured
  // when the drop STARTS, so a closure would compare against the drop-time
  // folder (the root you dropped into) rather than the folder you navigated to.
  const refreshFolder = useCallback(async (folderId: string | null) => {
    await queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', folderId], refetchType: 'none' })
    if (folderId === currentFolderIdRef.current) {
      await loadFiles(currentFolderIdRef.current)
    }
  }, [loadFiles, queryClient])

  const applyEventToLocalState = useCallback((event: FileEvent) => {
    const folder = currentFolderIdRef.current
    // Server emits the resolved papan-root id (`papan_<tenantId>`) for
    // drive-root events, but the client uses `null` for that folder.
    // Normalise to the cache convention so we match the open folder.
    const newParent = normalizeEventParentId(event.parentId)
    const oldParent = normalizeEventParentId(event.oldParentId)

    switch (event.type) {
      case 'file:created':
      case 'file:restored': {
        if (!event.file || newParent !== folder) return
        const item = eventRowToFileItem(event.file)
        const alreadyPresent = filesRef.current.some(f => f.id === item.id)
        setFiles(prev => prev.some(f => f.id === item.id) ? prev : sortFileItems([...prev, item], sortRef.current.sortBy, sortRef.current.sortDir))
        if (!alreadyPresent) setTotal(prev => prev + 1)
        return
      }
      case 'file:updated': {
        if (!event.file || newParent !== folder) return
        const item = eventRowToFileItem(event.file)
        setFiles(prev => {
          const i = prev.findIndex(f => f.id === item.id)
          if (i < 0) return prev
          const next = [...prev]
          // MERGE, don't replace: the event row carries only the storage
          // fields (name/size/metadataJson/…), NOT the listing-only enrichments
          // (hasOutboundShare, effectivePermission, sharedFromName, share* flags).
          // Replacing would drop the owner-side SHARING badge on an updated
          // `.miniapp` (e.g. after an icon edit) until a full refetch. Mirrors
          // the TanStack cache patch in `patchListsFromFileEvent`.
          next[i] = { ...next[i], ...item }
          return sortFileItems(next, sortRef.current.sortBy, sortRef.current.sortDir)
        })
        return
      }
      case 'file:moved': {
        if (!event.file) return
        const movedIn = newParent === folder && oldParent !== folder
        const movedOut = oldParent === folder && newParent !== folder
        const renamedHere = newParent === folder && oldParent === folder
        if (movedIn) {
          const item = eventRowToFileItem(event.file)
          const alreadyPresent = filesRef.current.some(f => f.id === item.id)
          setFiles(prev => prev.some(f => f.id === item.id) ? prev : sortFileItems([...prev, item], sortRef.current.sortBy, sortRef.current.sortDir))
          if (!alreadyPresent) setTotal(prev => prev + 1)
        } else if (movedOut) {
          const wasPresent = filesRef.current.some(f => f.id === event.fileId)
          setFiles(prev => prev.filter(f => f.id !== event.fileId))
          if (wasPresent) setTotal(prev => Math.max(0, prev - 1))
        } else if (renamedHere) {
          const item = eventRowToFileItem(event.file)
          setFiles(prev => {
            const i = prev.findIndex(f => f.id === item.id)
            if (i < 0) return prev
            const next = [...prev]
            next[i] = item
            return sortFileItems(next, sortRef.current.sortBy, sortRef.current.sortDir)
          })
        }
        return
      }
      case 'file:deleted': {
        if (newParent !== folder) return
        const wasPresent = filesRef.current.some(f => f.id === event.fileId)
        setFiles(prev => prev.filter(f => f.id !== event.fileId))
        if (wasPresent) setTotal(prev => Math.max(0, prev - 1))
        return
      }
    }
  }, [])

  // `refresh` closes over `currentFolderId` so its identity changes on
  // every folder navigation. Both subscriptions below route through this
  // ref to stay stable for the component's lifetime — folder nav doesn't
  // tear down and re-register the file-event or resync handlers.
  const refreshRef = useRef(refresh)
  refreshRef.current = refresh

  useEffect(() => {
    const off = onFileEvent((event) => {
      // Bin-only fan-out (`fanOutBinNudgeToRecipients` in
      // features/files/core/events.ts). The event reuses the
      // file:deleted/file:restored type so the bin listener can stay
      // simple, but it carries a source-tenant fileId that doesn't map
      // to anything in the drive cache. Skip so we don't trigger a
      // useless folder-list refetch — unless (#1209 I3) it is tagged
      // `sharedIn: true` and the open folder touches a share: it shows a
      // `.shared` row (its greyed state may flip), or it is inside a mount or
      // lists one's contents (compound `s_…` ids — the content may be gone).
      // A share root of this drive was binned or restored (an ancestor of the
      // shared folder included), and nothing else reaches the open folder for
      // an ancestor. Debounced like the fallback below; `FileListCacheSync`
      // marks the cached lists.
      if (isBinNudge(event)) {
        if (binNudgeSharedIn(event) !== true) return
        const open = currentFolderIdRef.current
        const touchesShare = isMountFileId(open)
          || (mountListingRef.current.folderId === open && mountListingRef.current.mount)
          || filesRef.current.some(f => f.type === 'shared' || isMountFileId(f.id))
        if (!touchesShare) return
      } else if (canPatchFileEvent(event)) {
        // Plain file/folder fast path — `FileListCacheSync` patches the cache;
        // splice the same row into the open folder's accumulator.
        // Pointer rows (`.shared`/`.chain`) deliberately return false from the
        // patcher: their per-recipient listing flags (shareBroken/shareNoAccess/
        // alreadyShared/chainBroken) AND effectivePermission can't ride the
        // broadcast payload, so they fall through to the debounced refresh below
        // and get recomputed server-side instead of flashing un-flagged.
        applyEventToLocalState(event)
        return
      }
      // Fallback path: server didn't include the row (older publisher) or
      // we lacked enough info to patch safely. Debounce + refetch matches
      // the pre-A.2 behavior, including the 500ms coalescing window for
      // bulk operations (copy/extract emit hundreds of events).
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null
        refreshRef.current()
      }, 500)
    })
    return () => {
      off()
      // Unmounted with a reload pending (sign-out, tenant switch): never fetch the old folder under the next scope.
      if (refreshTimerRef.current) { clearTimeout(refreshTimerRef.current); refreshTimerRef.current = null }
    }
  }, [onFileEvent, applyEventToLocalState])

  // #1264 — an action on a row of THIS folder was refused (it may be gone, its delete never
  // announced): re-read the open folder. Only when it lists the row — a refusal elsewhere is not ours.
  // Coalesced with the file-event refresh's own 500 ms timer: a bulk refusal re-reads once.
  useEffect(() => onListedRowRefused((fileId) => {
    if (!filesRef.current.some((f) => f.id === fileId)) return
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    refreshTimerRef.current = setTimeout(() => { refreshTimerRef.current = null; refreshRef.current() }, 500)
  }), [])

  // ─── Realtime resync (Phase A.3) ────────────────────────────────────
  //
  // The server emits `{type:'resync',kind:'file'}` when a reconnecting
  // client's `lastSeenFileSeq` doesn't match the current per-tenant
  // counter — i.e., events were published while the client's WS was
  // gone. The disconnect window may have dropped file:deleted /
  // file:moved events for folders the user isn't currently viewing —
  // those list caches now hold rows whose underlying ids no longer
  // exist. Navigating back to a stale folder then serves a dead id and
  // the SPA bounces. `FileListCacheSync` flushes every list cache, on any
  // page (#956); this reloads the open folder's accumulator, which the
  // cache flush doesn't reach.
  useEffect(() => {
    return onResync((kind) => {
      if (kind !== 'file') return
      refreshRef.current()
    })
  }, [onResync])

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore) return
    setLoadingMore(true)
    const seq = loadSeqRef.current   // #1316: a page of a list a newer load replaced is not appended to it
    try {
      const result = await fetchAndCacheList(queryClient, currentFolderId, {
        limit: PAGE_SIZE,
        offset: currentOffset,
        sortBy,
        sortDir,
      })
      if (seq !== loadSeqRef.current) return
      const newItems = result.files.map(fileListItemToFileItem)
      setFiles(prev => sortFileItems([...prev, ...newItems], sortBy, sortDir))
      setHasMore(result.hasMore)
      setTotal(result.total)
      setCurrentOffset(prev => prev + newItems.length)
    } catch (err) {
      // #1316 / #1210: a page dropped at a session end, or asked as an identity no longer active, adds nothing; the
      // next load reads as the current one
      if (!isSessionEndedError(err) && !(err instanceof IdentityChangedError)) throw err
    } finally {
      setLoadingMore(false)
    }
  }, [hasMore, loadingMore, currentFolderId, currentOffset, sortBy, sortDir, queryClient])

  const toggleSort = useCallback((field: SortField) => {
    const newDir = field === sortBy ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'
    setSortBy(field)
    setSortDir(newDir)
    loadFiles(currentFolderId, { sortBy: field, sortDir: newDir })
  }, [sortBy, sortDir, loadFiles, currentFolderId])

  /**
   * Upload a single file to the current folder
   */
  const safeUpload = useCallback(async (
    file: File,
    parentId: string | null,
    options?: {
      description?: string
      conflictStrategy?: ConflictStrategy
      onProgress?: (progress: UploadProgress) => void
      signal?: AbortSignal
    }
  ): Promise<UploadResult> => {
    try {
      const useProgress = options?.onProgress || file.size > 1024 * 1024

      if (useProgress && options?.onProgress) {
        const result = await filesApi.uploadFileWithProgress(parentId, file.name, file, {
          description: options.description,
          conflictStrategy: options.conflictStrategy,
          signal: options.signal,
          onProgress: (loaded, total) => {
            options.onProgress!({
              loaded,
              total,
              percentage: Math.round((loaded / total) * 100),
            })
          },
        })
        return { success: true, fileId: result.fileId, name: result.name, skipped: result.skipped }
      } else {
        const result = await filesApi.uploadFile(parentId, file.name, file, {
          description: options?.description,
          conflictStrategy: options?.conflictStrategy,
        })
        return { success: true, fileId: result.fileId, name: result.name, skipped: result.skipped }
      }
    } catch (err) {
      if (err instanceof MalwareDetectedError) {
        return { success: false, name: file.name, error: 'malware', threat: err.threat }
      }
      if (err instanceof Error && err.message === 'Upload cancelled') {
        return { success: false, name: file.name, error: 'cancelled' }
      }
      return { success: false, name: file.name, error: 'failed' }
    }
  }, [])

  const uploadFile = useCallback(async (file: File, description?: string): Promise<UploadResult> => {
    const result = await safeUpload(file, currentFolderId, { description })
    if (result.success) refresh()
    return result
  }, [currentFolderId, refresh, safeUpload])

  const uploadFileToFolder = useCallback(async (
    file: File,
    parentId: string | null,
    conflictStrategy?: ConflictStrategy,
    options?: {
      onProgress?: (progress: UploadProgress) => void
      signal?: AbortSignal
    }
  ): Promise<UploadResult> => {
    return safeUpload(file, parentId, { conflictStrategy, ...options })
  }, [safeUpload])

  /**
   * Upload multiple files with progress tracking
   */
  const uploadFiles = useCallback(async (
    filesToUpload: FileToUpload[],
    options?: {
      onProgress?: (current: number, total: number, fileName: string) => void
    }
  ): Promise<UploadFilesResult> => {
    const results: UploadResult[] = []
    let successCount = 0

    for (let i = 0; i < filesToUpload.length; i++) {
      const { file, parentId, conflictStrategy } = filesToUpload[i]
      options?.onProgress?.(i + 1, filesToUpload.length, file.name)

      const result = await safeUpload(file, parentId, { conflictStrategy })
      results.push(result)
      if (result.success && !result.skipped) {
        successCount++
      }
    }

    return { successCount, results }
  }, [safeUpload])

  /**
   * Upload multiple files with grouped progress tracking and cancellation support
   */
  const uploadFilesGrouped = useCallback(async (
    filesToUpload: FileToUpload[],
    groupController: UploadGroupController
  ): Promise<UploadFilesResult> => {
    const results: UploadResult[] = []
    let successCount = 0

    // Register all files with the group controller
    for (const { file } of filesToUpload) {
      groupController.addFile(file.name, file.name, file.size)
    }

    // Upload each file with progress tracking
    for (const { file, parentId, conflictStrategy } of filesToUpload) {
      const fileKey = file.name
      const abortController = groupController.getAbortController(fileKey)

      try {
        const uploadResult = await filesApi.uploadFileWithProgress(parentId, file.name, file, {
          conflictStrategy,
          signal: abortController.signal,
          uploadId: fileKey,
          onProgress: (loaded) => {
            groupController.updateFile(fileKey, loaded)
          },
        })

        if (uploadResult.skipped) {
          groupController.completeFile(fileKey)
          results.push({ success: true, fileId: uploadResult.fileId, name: uploadResult.name, skipped: true })
        } else {
          groupController.completeFile(fileKey)
          results.push({ success: true, fileId: uploadResult.fileId, name: uploadResult.name })
          successCount++
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          groupController.cancelFile(fileKey)
          results.push({ success: false, name: file.name, error: 'cancelled' })
        } else if (err instanceof MalwareDetectedError) {
          groupController.failFile(fileKey, `Malware: ${err.threat}`)
          results.push({ success: false, name: file.name, error: 'malware', threat: err.threat })
        } else {
          const errorMsg = err instanceof Error ? err.message : 'Upload failed'
          groupController.failFile(fileKey, errorMsg)
          results.push({ success: false, name: file.name, error: 'failed' })
        }
      }
    }

    return { successCount, results }
  }, [])

  const moveFile = useCallback(async (fileId: string, update: { name?: string; parentId?: string | null }) => {
    const result = await filesApi.moveFile(fileId, update)
    refresh()
    return result
  }, [refresh])

  const createFolder = useCallback(async (name: string) => {
    const result = await filesApi.createFolder(currentFolderId, name)
    refresh()
    return result
  }, [currentFolderId, refresh])

  return {
    files,
    currentFolderId,
    loading,
    loadingMore,
    error,
    hasMore,
    total,
    sortBy,
    sortDir,
    parentPermission,
    parentReshareBlocked,
    parentNeedsApproval,
    navigate,
    refresh,
    refreshFolder,
    loadMore,
    toggleSort,
    uploadFile,
    uploadFileToFolder,
    uploadFiles,
    uploadFilesGrouped,
    moveFile,
    createFolder,
  }
}

/** #894: one Bin section as loaded so far — `'mine'` or a source tenant id. */
export interface BinSectionState {
  /** Display name of a Shared Bin source; absent for My Bin. */
  name?: string
  items: BinItem[]
  /** Entries this caller can see in the section (`totalIsLowerBound`: at least). */
  total: number
  totalIsLowerBound: boolean
  nextCursor: string | null
  loadingMore: boolean
}

type BinSections = Record<string, BinSectionState>

/** Newest first, as the server orders: `deleted_at` desc, then id desc. */
function binRowIsOlder(a: BinItem, b: BinItem): boolean {
  return a.deletedAt < b.deletedAt || (a.deletedAt === b.deletedAt && a.id < b.id)
}

/**
 * Lay a refetched FIRST page over a section that may hold more pages (#894): a
 * change elsewhere refetches only the first page, never the whole Bin. The
 * fresh page replaces the head; rows already loaded past it stay, with their
 * cursor. A section that now fits one page is the fresh page alone.
 */
export function mergeBinFirstPage(old: BinSectionState | undefined, fresh: Omit<BinSectionState, 'loadingMore'>): BinSectionState {
  const last = fresh.items.at(-1)
  if (!old || fresh.nextCursor === null || !last || old.items.length <= fresh.items.length) {
    return { ...fresh, loadingMore: old?.loadingMore ?? false }
  }
  const freshIds = new Set(fresh.items.map(i => i.id))
  const tail = old.items.filter(i => !freshIds.has(i.id) && binRowIsOlder(i, last))
  return { ...fresh, items: [...fresh.items, ...tail], nextCursor: tail.length > 0 ? old.nextCursor : fresh.nextCursor, loadingMore: old.loadingMore }
}

function sectionsFromResponse(r: BinResponse): Record<string, Omit<BinSectionState, 'loadingMore'>> {
  // An older server sends every entry and no paging fields: total = what came, no next page.
  const out: Record<string, Omit<BinSectionState, 'loadingMore'>> = {
    mine: {
      items: r.myBin,
      total: r.myBinPage?.total ?? r.myBin.length,
      totalIsLowerBound: r.myBinPage?.totalIsLowerBound ?? false,
      nextCursor: r.myBinPage?.nextCursor ?? null,
    },
  }
  for (const [tenantId, g] of Object.entries(r.sharedBin.byTenant)) {
    out[tenantId] = {
      name: g.name,
      items: g.items,
      total: g.total ?? g.items.length,
      totalIsLowerBound: g.totalIsLowerBound ?? false,
      nextCursor: g.nextCursor ?? null,
    }
  }
  return out
}

const EMPTY_MINE: BinSectionState = { items: [], total: 0, totalIsLowerBound: false, nextCursor: null, loadingMore: false }

/**
 * The Bin, a page at a time (#894). Opens on each section's first page with its
 * total; `loadMore(section)` appends the next page. A change (a delete or
 * restore anywhere, a background restore's announcement) refetches only the
 * first pages and merges them in — never the whole Bin.
 */
/**
 * #969: an Empty Bin running for this tenant. `owned`: THIS tab started (or
 * joined) it and follows it to its end with a tracker; otherwise the page shows
 * it from the listing and the job's progress events.
 */
export interface BinEmptyingState {
  jobId: string
  operationId: string
  done: number
  total: number
  owned: boolean
}

/** How often a page that did not start the Empty Bin re-asks the listing while it runs. */
const EMPTYING_REFRESH_MS = 30_000

export function useBin() {
  const [sections, setSections] = useState<BinSections>({ mine: EMPTY_MINE })
  const [loading, setLoading] = useState(false)
  const [emptying, setEmptying] = useState<BinEmptyingState | null>(null)
  const { onFileEvent, onResync, onOperationEvent, status } = useRealtime()
  const statusRef = useRef(status)
  statusRef.current = status

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const res = await filesApi.listBin()
      const running = res.emptying
      // The listing is the truth for a job this tab does not follow; one it
      // follows ends when its tracker says so.
      setEmptying(prev => running
        ? { jobId: running.jobId, operationId: running.operationId, total: running.total,
            done: prev?.jobId === running.jobId ? prev.done : 0, owned: prev?.jobId === running.jobId && prev.owned }
        : (prev?.owned ? prev : null))
      const fresh = sectionsFromResponse(res)
      setSections(prev => {
        const next: BinSections = {}
        // A Shared Bin source that has nothing visible any more is left out by
        // the server; it goes here too.
        for (const [key, page] of Object.entries(fresh)) next[key] = mergeBinFirstPage(prev[key], page)
        return next
      })
    } catch (err) {
      // #1316: dropped at a session end — the Bin sits in a remounting deck, so the remount reloads it; nothing is written
      if (isSessionEndedError(err)) return
      setSections({ mine: EMPTY_MINE })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  // The sections as last rendered, and the sections with a page request out —
  // read synchronously by `loadMore` (a state updater may run later).
  const sectionsRef = useRef(sections)
  sectionsRef.current = sections
  const loadingMoreRef = useRef(new Set<string>())

  const loadMore = useCallback(async (section: string) => {
    const cur = sectionsRef.current[section]
    if (!cur?.nextCursor || loadingMoreRef.current.has(section)) return
    const asked = cur.nextCursor
    loadingMoreRef.current.add(section)
    setSections(prev => prev[section] ? { ...prev, [section]: { ...prev[section]!, loadingMore: true } } : prev)
    try {
      const page = await filesApi.listBinSection(section, asked)
      setSections(prev => {
        const cur = prev[section]
        // A refresh in between may have reset the section; only append to the page we asked after.
        if (!cur || cur.nextCursor !== asked) return cur ? { ...prev, [section]: { ...cur, loadingMore: false } } : prev
        const have = new Set(cur.items.map(i => i.id))
        return { ...prev, [section]: { ...cur, items: [...cur.items, ...page.items.filter(i => !have.has(i.id))], nextCursor: page.nextCursor, loadingMore: false } }
      })
    } catch {
      setSections(prev => prev[section] ? { ...prev, [section]: { ...prev[section]!, loadingMore: false } } : prev)
    } finally {
      loadingMoreRef.current.delete(section)
    }
  }, [])

  /** Take an entry out of whichever section holds it, and its count with it. */
  const dropLocally = useCallback((binId: string) => {
    setSections(prev => {
      let changed = false
      const next: BinSections = {}
      for (const [key, sec] of Object.entries(prev)) {
        const items = sec.items.filter(i => i.id !== binId)
        if (items.length !== sec.items.length) {
          changed = true
          next[key] = { ...sec, items, total: Math.max(0, sec.total - 1) }
        } else next[key] = sec
      }
      return changed ? next : prev
    })
  }, [])

  // Realtime: drive-side deletes/restores happen in `useFiles` (the source of
  // the events here). The Bin isn't in the TanStack list cache, so a change is
  // a debounced refetch — of the first pages only (#894), merged into what is
  // loaded. Per-tenant subscription means `sharedBin` (events emitted by other
  // tenants) arrives through the bin nudge or a manual refresh.
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    const off = onFileEvent((event) => {
      if (event.type !== 'file:deleted' && event.type !== 'file:restored') return
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = setTimeout(() => { refresh() }, 250)
    })
    // #854: a background restore announces itself to the requester's tenant when
    // queued (the row turns "Restoring…" in every window), and again when it ends
    // either way. `completed` counts too: `file:restored` goes to the tenant that
    // OWNS the item, so for a Shared Bin entry the requester's other windows would
    // never hear it and keep the row marked.
    const offOperation = onOperationEvent((event) => {
      // #969: an Empty Bin's progress, in every window of the tenant.
      if (event.kind === 'empty-bin' && event.stage === 'processing' && event.jobId) {
        const jobId = event.jobId
        setEmptying(prev => (prev && prev.jobId !== jobId) ? prev : {
          jobId, operationId: event.operationId, owned: prev?.owned ?? false,
          done: event.done ?? prev?.done ?? 0, total: event.total || prev?.total || 0,
        })
        return
      }
      if (event.kind === 'empty-bin' && (event.stage === 'completed' || event.stage === 'failed')) {
        setEmptying(prev => (prev && !prev.owned && prev.jobId === event.jobId) ? null : prev)
      }
      // #1167 S2: a Delete forever job queued or ended — in every window of the tenant.
      if (event.kind !== 'restore' && event.kind !== 'empty-bin' && event.kind !== 'bin-purge') return
      if (event.stage !== 'pending' && event.stage !== 'failed' && event.stage !== 'completed') return
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = setTimeout(() => { refresh() }, 250)
    })
    const offResync = onResync((kind) => {
      if (kind === 'file') refresh()
    })
    return () => {
      off()
      offOperation()
      offResync()
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    }
  }, [onFileEvent, onResync, onOperationEvent, refresh])

  // Whatever happened while the socket was not up — before it first connected
  // (the listing is read on mount, the socket opens later) or during a
  // reconnect — reached this page as no event at all. An Empty Bin that ended
  // in that gap would leave the page on its old entries for good (#969: the
  // listing didn't say "emptying" yet, so no poll runs). So each time the socket
  // comes up, the listing is read again.
  const wasConnectedRef = useRef(status === 'connected')
  useEffect(() => {
    const connected = status === 'connected'
    if (connected && !wasConnectedRef.current) {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = setTimeout(() => { refresh() }, 250)
    }
    wasConnectedRef.current = connected
  }, [status, refresh])

  /**
   * Delete forever on one entry (#1167 S2): inline for a small entry, as the
   * `bin-purge-item` job for a big one (`runBinPurgeJob`; `hooks.onQueued` says
   * when it was queued, for the caller's progress toast). Resolves once the entry
   * is gone; rejects with the request's refusal or the job's `JobFailedError` /
   * `JobLostError`. The listing is read again either way.
   */
  const deletePermanently = useCallback(async (binId: string, hooks?: BinPurgeRunHooks): Promise<'inline' | 'job'> => {
    try {
      const how = await runBinPurgeJob(binId, {
        start: (id, operationId) => filesApi.deleteBinItemAsync(id, operationId),
        getStatus: (jobId) => filesApi.getBinPurgeStatus(jobId),
        onOperationEvent,
        isConnected: () => statusRef.current === 'connected',
      }, hooks)
      dropLocally(binId)
      return how
    } finally {
      refresh()
    }
  }, [refresh, dropLocally, onOperationEvent])

  // #969: a page that did not start the Empty Bin re-asks the listing now and
  // then while it runs — the safety net if its last progress event never came.
  const emptyingNotOwned = emptying && !emptying.owned ? emptying.jobId : null
  useEffect(() => {
    if (!emptyingNotOwned) return
    const timer = setInterval(() => { refresh() }, EMPTYING_REFRESH_MS)
    return () => clearInterval(timer)
  }, [emptyingNotOwned, refresh])

  /**
   * #969: Empty Bin as a background job (`runEmptyBinJob`). Resolves with
   * the entries removed when the job ends (`onProgress` on the way); rejects
   * with the tracker's `JobFailedError` / `JobLostError`, or the request's error.
   */
  const emptyBin = useCallback(async (onProgress?: (done: number, total: number) => void): Promise<number> => {
    let jobId: string | null = null
    try {
      return await runEmptyBinJob({
        start: (operationId) => filesApi.emptyBinAsync(operationId),
        getStatus: (jobId) => filesApi.getEmptyBinStatus(jobId),
        onOperationEvent,
        isConnected: () => statusRef.current === 'connected',
      }, {
        onQueued: (job) => {
          jobId = job.jobId
          setEmptying({ jobId: job.jobId, operationId: job.operationId, done: 0, total: job.total, owned: true })
        },
        onProgress: (done, total) => {
          setEmptying(prev => prev && prev.jobId === jobId ? { ...prev, done, total: total || prev.total } : prev)
          onProgress?.(done, total)
        },
      })
    } finally {
      setEmptying(prev => (prev && prev.jobId === jobId ? null : prev))
      refresh()
    }
  }, [refresh, onOperationEvent])

  const dismiss = useCallback(async (binId: string) => {
    await filesApi.dismissBinItem(binId)
    dropLocally(binId)
    refresh()
  }, [refresh, dropLocally])

  const mine = sections.mine ?? EMPTY_MINE
  const sharedBinByTenant: Record<string, BinSectionState & { name: string }> = {}
  for (const [key, sec] of Object.entries(sections)) {
    if (key !== 'mine') sharedBinByTenant[key] = { ...sec, name: sec.name ?? key }
  }

  return {
    myBin: mine.items,
    /** #894: My Bin's total, whether it is a lower bound, and whether more pages follow. */
    myBinSection: mine,
    sharedBinByTenant,
    loading,
    refresh,
    loadMore,
    deletePermanently,
    emptyBin,
    /** #969: an Empty Bin running for this tenant, or null. */
    emptying,
    dismiss,
  }
}

export function useQuarantine() {
  const { items, loading, refresh } = useAsyncList(filesApi.listQuarantine)

  const deleteItem = useCallback(async (itemId: string) => {
    await filesApi.deleteQuarantineItem(itemId)
    refresh()
  }, [refresh])

  const restoreItem = useCallback(async (itemId: string) => {
    const result = await filesApi.restoreQuarantineItem(itemId)
    refresh()
    return result
  }, [refresh])

  const downloadItem = useCallback(async (itemId: string) => {
    await filesApi.downloadQuarantineItem(itemId)
  }, [])

  return { items, loading, refresh, deleteItem, restoreItem, downloadItem }
}
